// src/components/settings/shared/PaymentsSection.jsx
import React from 'react';
import { CreditCard, DollarSign, Shield, Settings } from 'lucide-react';
import { FormField, TextInput, SelectInput, Toggle } from './FormComponents';

export const PaymentsSection = ({ formData, onInputChange, templateType }) => {
  // Helper function to get nested payment setting value
  const getPaymentValue = (field) => {
    return formData.paymentSettings?.[field] ?? '';
  };

  // Helper function to set nested payment setting value
  const setPaymentValue = (field, value) => {
    onInputChange(`paymentSettings.${field}`, value);
  };

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' },
    { value: 'AUD', label: 'Australian Dollar (A$)' },
    { value: 'ZAR', label: 'South African Rand (R)' }
  ];

  const paymentProcessors = [
    { value: 'stripe', label: 'Stripe' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'square', label: 'Square' },
    { value: 'payfast', label: 'PayFast (South Africa)' },
    { value: 'manual', label: 'Manual Processing' }
  ];

  const taxTypes = [
    { value: 'none', label: 'No Tax' },
    { value: 'inclusive', label: 'Tax Inclusive' },
    { value: 'exclusive', label: 'Tax Exclusive' },
    { value: 'vat', label: 'VAT' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Settings</h3>
        <p className="text-gray-600">
          Configure payment processing and pricing options
        </p>
      </div>

      {/* Payment Processing */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Payment Processing</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Payment Processor">
            <SelectInput
              value={getPaymentValue('paymentProcessor')}
              onChange={(value) => setPaymentValue('paymentProcessor', value)}
              options={paymentProcessors}
              placeholder="Select payment processor"
            />
          </FormField>

          <FormField label="Default Currency">
            <SelectInput
              value={getPaymentValue('currency')}
              onChange={(value) => setPaymentValue('currency', value)}
              options={currencyOptions}
              placeholder="Select currency"
            />
          </FormField>
        </div>

        {/* Stripe Configuration */}
        {getPaymentValue('paymentProcessor') === 'stripe' && (
          <div className="bg-blue-50 p-4 rounded-lg space-y-4">
            <h5 className="font-medium text-blue-900">Stripe Configuration</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Stripe Publishable Key">
                <TextInput
                  value={getPaymentValue('stripePublishableKey')}
                  onChange={(value) => setPaymentValue('stripePublishableKey', value)}
                  placeholder="pk_live_..."
                  type="password"
                />
              </FormField>
              <FormField label="Stripe Secret Key">
                <TextInput
                  value={getPaymentValue('stripeSecretKey')}
                  onChange={(value) => setPaymentValue('stripeSecretKey', value)}
                  placeholder="sk_live_..."
                  type="password"
                />
              </FormField>
            </div>
          </div>
        )}

        {/* PayPal Configuration */}
        {getPaymentValue('paymentProcessor') === 'paypal' && (
          <div className="bg-yellow-50 p-4 rounded-lg space-y-4">
            <h5 className="font-medium text-yellow-900">PayPal Configuration</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="PayPal Client ID">
                <TextInput
                  value={getPaymentValue('paypalClientId')}
                  onChange={(value) => setPaymentValue('paypalClientId', value)}
                  placeholder="Your PayPal Client ID"
                />
              </FormField>
              <FormField label="PayPal Client Secret">
                <TextInput
                  value={getPaymentValue('paypalClientSecret')}
                  onChange={(value) => setPaymentValue('paypalClientSecret', value)}
                  placeholder="Your PayPal Client Secret"
                  type="password"
                />
              </FormField>
            </div>
          </div>
        )}

        {/* Square Configuration */}
        {getPaymentValue('paymentProcessor') === 'square' && (
          <div className="bg-green-50 p-4 rounded-lg space-y-4">
            <h5 className="font-medium text-green-900">Square Configuration</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Application ID">
                <TextInput
                  value={getPaymentValue('squareApplicationId')}
                  onChange={(value) => setPaymentValue('squareApplicationId', value)}
                  placeholder="Your Square Application ID"
                />
              </FormField>
              <FormField label="Access Token">
                <TextInput
                  value={getPaymentValue('squareAccessToken')}
                  onChange={(value) => setPaymentValue('squareAccessToken', value)}
                  placeholder="Your Square Access Token"
                  type="password"
                />
              </FormField>
            </div>
            <FormField label="Location ID">
              <TextInput
                value={getPaymentValue('squareLocationId')}
                onChange={(value) => setPaymentValue('squareLocationId', value)}
                placeholder="Your Square Location ID"
              />
            </FormField>
          </div>
        )}

        {/* PayFast Configuration */}
        {getPaymentValue('paymentProcessor') === 'payfast' && (
          <div className="bg-orange-50 p-4 rounded-lg space-y-4">
            <h5 className="font-medium text-orange-900">PayFast Configuration (South Africa)</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Merchant ID">
                <TextInput
                  value={getPaymentValue('payfastMerchantId')}
                  onChange={(value) => setPaymentValue('payfastMerchantId', value)}
                  placeholder="Your PayFast Merchant ID"
                />
              </FormField>
              <FormField label="Merchant Key">
                <TextInput
                  value={getPaymentValue('payfastMerchantKey')}
                  onChange={(value) => setPaymentValue('payfastMerchantKey', value)}
                  placeholder="Your PayFast Merchant Key"
                  type="password"
                />
              </FormField>
            </div>
            <FormField label="Passphrase">
              <TextInput
                value={getPaymentValue('payfastPassphrase')}
                onChange={(value) => setPaymentValue('payfastPassphrase', value)}
                placeholder="Your PayFast Passphrase"
                type="password"
              />
            </FormField>
            <FormField label="Sandbox Mode">
              <Toggle
                checked={getPaymentValue('payfastSandbox')}
                onChange={(value) => setPaymentValue('payfastSandbox', value)}
                label="Enable sandbox mode for testing"
              />
            </FormField>
          </div>
        )}
      </div>

      {/* Pricing & Fees */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Pricing & Fees</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Processing Fee (%)">
            <TextInput
              type="number"
              value={getPaymentValue('processingFeePercent')}
              onChange={(value) => setPaymentValue('processingFeePercent', parseFloat(value))}
              placeholder="2.9"
              step="0.1"
            />
          </FormField>

          <FormField label="Fixed Fee">
            <TextInput
              type="number"
              value={getPaymentValue('fixedFee')}
              onChange={(value) => setPaymentValue('fixedFee', parseFloat(value))}
              placeholder="0.30"
              step="0.01"
            />
          </FormField>

          <FormField label="Minimum Amount">
            <TextInput
              type="number"
              value={getPaymentValue('minimumAmount')}
              onChange={(value) => setPaymentValue('minimumAmount', parseFloat(value))}
              placeholder="1.00"
              step="0.01"
            />
          </FormField>
        </div>

        {/* Tax Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Tax Type">
            <SelectInput
              value={getPaymentValue('taxType')}
              onChange={(value) => setPaymentValue('taxType', value)}
              options={taxTypes}
              placeholder="Select tax type"
            />
          </FormField>

          {getPaymentValue('taxType') !== 'none' && (
            <>
              <FormField label="Tax Rate (%)">
                <TextInput
                  type="number"
                  value={getPaymentValue('taxRate')}
                  onChange={(value) => setPaymentValue('taxRate', parseFloat(value))}
                  placeholder="15"
                  step="0.1"
                />
              </FormField>

              <FormField label="Tax Label">
                <TextInput
                  value={getPaymentValue('taxLabel')}
                  onChange={(value) => setPaymentValue('taxLabel', value)}
                  placeholder="VAT / Tax / GST"
                />
              </FormField>

              <FormField label="Tax Number">
                <TextInput
                  value={getPaymentValue('taxNumber')}
                  onChange={(value) => setPaymentValue('taxNumber', value)}
                  placeholder="Your tax registration number"
                />
              </FormField>
            </>
          )}
        </div>
      </div>

      {/* Payment Options */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Accepted Payment Methods</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Credit Cards">
            <Toggle
              checked={getPaymentValue('acceptCreditCards')}
              onChange={(value) => setPaymentValue('acceptCreditCards', value)}
              label="Accept credit card payments"
            />
          </FormField>

          <FormField label="Debit Cards">
            <Toggle
              checked={getPaymentValue('acceptDebitCards')}
              onChange={(value) => setPaymentValue('acceptDebitCards', value)}
              label="Accept debit card payments"
            />
          </FormField>

          <FormField label="Apple Pay">
            <Toggle
              checked={getPaymentValue('acceptApplePay')}
              onChange={(value) => setPaymentValue('acceptApplePay', value)}
              label="Enable Apple Pay"
            />
          </FormField>

          <FormField label="Google Pay">
            <Toggle
              checked={getPaymentValue('acceptGooglePay')}
              onChange={(value) => setPaymentValue('acceptGooglePay', value)}
              label="Enable Google Pay"
            />
          </FormField>

          <FormField label="PayPal">
            <Toggle
              checked={getPaymentValue('acceptPayPal')}
              onChange={(value) => setPaymentValue('acceptPayPal', value)}
              label="Enable PayPal"
            />
          </FormField>

          <FormField label="Bank Transfer">
            <Toggle
              checked={getPaymentValue('acceptBankTransfer')}
              onChange={(value) => setPaymentValue('acceptBankTransfer', value)}
              label="Accept bank transfers"
            />
          </FormField>

          <FormField label="Cryptocurrency">
            <Toggle
              checked={getPaymentValue('acceptCrypto')}
              onChange={(value) => setPaymentValue('acceptCrypto', value)}
              label="Accept crypto payments"
            />
          </FormField>
        </div>
      </div>

      {/* Security & Compliance */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Security & Compliance</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="3D Secure">
            <Toggle
              checked={getPaymentValue('require3DSecure')}
              onChange={(value) => setPaymentValue('require3DSecure', value)}
              label="Require 3D Secure authentication"
            />
          </FormField>

          <FormField label="Fraud Detection">
            <Toggle
              checked={getPaymentValue('enableFraudDetection')}
              onChange={(value) => setPaymentValue('enableFraudDetection', value)}
              label="Enable fraud detection"
            />
          </FormField>

          <FormField label="PCI Compliant">
            <Toggle
              checked={getPaymentValue('pciCompliant')}
              onChange={(value) => setPaymentValue('pciCompliant', value)}
              label="PCI DSS compliant"
            />
          </FormField>

          <FormField label="SSL Enabled">
            <Toggle
              checked={getPaymentValue('sslEnabled')}
              onChange={(value) => setPaymentValue('sslEnabled', value)}
              label="SSL encryption enabled"
            />
          </FormField>
        </div>
      </div>

      {/* Refunds & Disputes */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Refunds & Disputes</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Allow Refunds">
            <Toggle
              checked={getPaymentValue('allowRefunds')}
              onChange={(value) => setPaymentValue('allowRefunds', value)}
              label="Enable refund requests"
            />
          </FormField>

          {getPaymentValue('allowRefunds') && (
            <>
              <FormField label="Refund Period (Days)">
                <TextInput
                  type="number"
                  value={getPaymentValue('refundPeriodDays')}
                  onChange={(value) => setPaymentValue('refundPeriodDays', parseInt(value))}
                  placeholder="30"
                />
              </FormField>

              <FormField label="Auto-Refund Failed Payments">
                <Toggle
                  checked={getPaymentValue('autoRefundFailed')}
                  onChange={(value) => setPaymentValue('autoRefundFailed', value)}
                  label="Automatically refund failed payments"
                />
              </FormField>

              <FormField label="Dispute Resolution">
                <SelectInput
                  value={getPaymentValue('disputeResolution')}
                  onChange={(value) => setPaymentValue('disputeResolution', value)}
                  options={[
                    { value: 'manual', label: 'Manual Review' },
                    { value: 'automatic', label: 'Automatic' },
                    { value: 'escalate', label: 'Escalate to Support' }
                  ]}
                  placeholder="Select resolution method"
                />
              </FormField>
            </>
          )}
        </div>
      </div>

      {/* Payment Notifications */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Payment Notifications</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Payment Notifications">
            <Toggle
              checked={getPaymentValue('notifyOnPayment')}
              onChange={(value) => setPaymentValue('notifyOnPayment', value)}
              label="Notify on successful payments"
            />
          </FormField>

          <FormField label="Refund Notifications">
            <Toggle
              checked={getPaymentValue('notifyOnRefund')}
              onChange={(value) => setPaymentValue('notifyOnRefund', value)}
              label="Notify on refunds"
            />
          </FormField>

          <FormField label="Dispute Notifications">
            <Toggle
              checked={getPaymentValue('notifyOnDispute')}
              onChange={(value) => setPaymentValue('notifyOnDispute', value)}
              label="Notify on disputes"
            />
          </FormField>

          <FormField label="Notification Email">
            <TextInput
              type="email"
              value={getPaymentValue('paymentNotificationEmail')}
              onChange={(value) => setPaymentValue('paymentNotificationEmail', value)}
              placeholder="admin@example.com"
            />
          </FormField>
        </div>
      </div>

      {/* Additional Features */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Additional Features</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Save Cards">
            <Toggle
              checked={getPaymentValue('enableSaveCards')}
              onChange={(value) => setPaymentValue('enableSaveCards', value)}
              label="Allow customers to save payment methods"
            />
          </FormField>

          <FormField label="Auto-Charge">
            <Toggle
              checked={getPaymentValue('enableAutoCharge')}
              onChange={(value) => setPaymentValue('enableAutoCharge', value)}
              label="Enable automatic charging"
            />
          </FormField>

          <FormField label="Subscriptions">
            <Toggle
              checked={getPaymentValue('enableSubscriptions')}
              onChange={(value) => setPaymentValue('enableSubscriptions', value)}
              label="Enable subscription billing"
            />
          </FormField>

          <FormField label="Payment Plans">
            <Toggle
              checked={getPaymentValue('enablePaymentPlans')}
              onChange={(value) => setPaymentValue('enablePaymentPlans', value)}
              label="Enable installment payment plans"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};